import { Building2Icon, ClockIcon, NetworkIcon } from 'assets/icons';
import AssignMenu from 'components/assign/AssignMenu';
import { kanbanDateText } from 'helper/taskDates';
import useText from 'hooks/useText';
import TaskTypeBadge from './TaskTypeBadge';

/**
 * A task of the kanban: its title and assignee, its company, its type and its
 * due day (in red when late). It is dragged to another column; a click opens
 * it. `data-id` lets the board make it glide (hooks/useFlipLayout).
 */
const TaskKanbanCard = ({
  row,
  dragged,
  team,
  meId,
  onDragStart,
  onDragEnd,
  onOpen,
  onAssign,
}) => {
  const { text } = useText('tasks');
  const late = row.late && !row.doneAt;
  const place = row.company
    ? { Icon: Building2Icon, name: row.company.name }
    : row.group
      ? { Icon: NetworkIcon, name: row.group.name }
      : null;
  return (
    <article
      data-id={row.key}
      draggable
      onDragStart={(event) => onDragStart(event, row)}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(row)}
      className={`cursor-pointer rounded-lg border border-gray-200 bg-white p-3 transition hover:border-gray-300 ${
        dragged ? 'opacity-50' : ''
      }`}
    >
      {/* The company sits right under the title, in its column: the avatar
        beside them, taller than a line, no longer pushes it down. */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p
            className={`text-sm font-medium ${row.doneAt ? 'text-gray-400 line-through' : 'text-gray-900'}`}
          >
            {row.title}
          </p>
          {place && (
            <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-600">
              <span className="flex shrink-0">
                <place.Icon wh={16} color="currentColor" />
              </span>
              <span className="truncate">{place.name}</span>
            </p>
          )}
        </div>
        <span
          className="flex shrink-0"
          onClick={(event) => event.stopPropagation()}
        >
          <AssignMenu
            value={row.assigneeId}
            employees={team}
            meId={meId}
            onChange={(assigneeId) => onAssign(row, assigneeId)}
          />
        </span>
      </div>
      {/* The type on the left, the due day on the right. */}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-1.5">
        <TaskTypeBadge row={row} />
        <span
          className={`flex items-center gap-1 text-xs ${late ? 'text-red-600' : 'text-gray-600'}`}
        >
          <ClockIcon wh={14} color="currentColor" />
          {kanbanDateText(row, text)}
        </span>
      </div>
    </article>
  );
};

export default TaskKanbanCard;
